import { connect } from "react-redux";
import { Button, Col, Row, Toast } from "reactstrap";
import { AnyAction, bindActionCreators, Dispatch } from "redux";
import { thunkCreateReversalInteraction, thunkResetInteraction } from "../../store/actions/TouchpointActions";
import { BonusResponseDto, CouponDto } from "../../store/models/TouchpointDto";
import { IStore } from "../../store/rootReducer";
import { formatEuro, formatGermanDate, formatGermanTime, getCurrentDateTimeAsString, getRandomId } from "../app/Utils";
import { calculateCoupon, calculatePaymentAmountWithCouponAndXpress, calculateNewCouponSavings } from "./CashDeskSimulator";
import { pathToTheme } from "../../themes/SwitchTheme";
import { IssuedCouponDto } from "../../store/models/CouponTemplateDto";

// Helper function to format new API coupon display name for receipt
const formatCouponForReceipt = (coupon: IssuedCouponDto): string => {
  const template = coupon.couponTemplate;
  if (!template) return 'Coupon';
  
  // Clean up the displayed name
  let name = template.displayedCouponName || template.valueType?.replaceAll("_", " ") || 'Coupon';
  
  // Remove generic prefixes that aren't user-friendly
  name = name
    .replace(/^GENERIC CODE:\s*/i, "")
    .replace(/^CODE:\s*/i, "")
    .replace(/^COUPON:\s*/i, "")
    .trim();
  
  // Add discount information for better clarity
  if (template.valueType === "PERCENTAGE" && template.discountPercentage) {
    return `${name} (${template.discountPercentage}% Rabatt)`;
  } else if (template.valueType === "FIXED_AMOUNT" && template.discountAmount) {
    return `${name} (${formatEuro(template.discountAmount)} Rabatt)`;
  } else if (template.valueType === "FREE_SHIPPING") {
    return `${name} (Versandkostenfrei)`;
  } else if (template.valueType === "GIFT") {
    return `${name} (Geschenk)`;
  }
  
  return name;
};

// Helper function to format old coupon system display name for receipt
const formatOldCouponForReceipt = (coupon: CouponDto): string => {
  let name = coupon.advantage.advantageText || 'Coupon';
  
  // Clean up the advantage text
  name = name
    .replace(/^GENERIC CODE:\s*/i, "")
    .replace(/^CODE:\s*/i, "")
    .replace(/^COUPON:\s*/i, "")
    .trim();
  
  // Add discount type information for better clarity
  if (coupon.advantage.advantageType === "PERCENT") {
    return `${name} (${coupon.advantage.advantageValue}% Rabatt)`;
  } else if (coupon.advantage.advantageType === "EURO") {
    return `${name} (${formatEuro(coupon.advantage.advantageValue)} Rabatt)`;
  } else if (coupon.advantage.advantageType === "OFFER") {
    return name; // For offers, the text is usually descriptive enough
  }
  
  return name;
};

type ThunkProps = ReturnType<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

const getThemeDisplayName = (theme: string) => {
  const themeNames: { [key: string]: string } = {
    pali: "Pali",
    atu: "A.T.U",
    seaside: "Seaside No.1",
    weltbild: "Weltbild",
    popken: "Ulla Popken",
    bijou: "Bijou Brigitte",
    tamaris: "Tamaris",
    poco: "POCO",
    conrad: "Conrad",
    hagebau: "Hagebau",
    futterhaus: "Futterhaus",
    wolford: "Wolford",
    toom: "Toom",
    engelhorn: "Engelhorn",
    konsum: "Konsum",
    knistr: "Knistr",
    marcopolo: "Marc O'Polo",
    nutripur: "NutriPur",
  };
  return themeNames[theme] || theme;
};

const ReceiptSimulator = (props: ThunkProps) => {
  const { interaction, operationalUnits } = props;

  const formatBonusPoints = (paymentResponse?: BonusResponseDto, reversalResponse?: BonusResponseDto) => {
    const bonusPoints = reversalResponse ? -Number(reversalResponse.bonusPoints) : Number(paymentResponse?.bonusPoints);
    const bonusPointText = `${bonusPoints || "keine"} Bonuspunkt${bonusPoints !== 1 && "e"}`;
    return (reversalResponse ? `Wir haben ${bonusPointText} storniert.` : `Du sammelst ${bonusPointText}.`).padEnd(
      45,
      "\xa0"
    );
  };

  const formatTotalBonusPoints = (paymentResponse?: BonusResponseDto, reversalResponse?: BonusResponseDto): string => {
    const response = reversalResponse || paymentResponse;
    if (response?.totalBonusPoints === undefined) {
      return `Dein Punktestand beträgt nun 0.`.padEnd(45, "\xa0");
    }
    const totalBonusPoints = Number(response.totalBonusPoints);
    return `Dein Punktestand beträgt nun ${totalBonusPoints}.`.padEnd(45, "\xa0");
  };

  const reversal = (hasExternalReferenceReceiptId: boolean) => {
    props.thunkCreateReversalInteraction(
      {
        externalCustomerId: interaction.externalCustomerId,
        customerNumber: interaction.customerNumber,
        externalReceiptId: getRandomId(),
        externalReferenceReceiptId: hasExternalReferenceReceiptId ? interaction.externalReceiptId : undefined,
        externalUnitNumber: interaction.externalUnitNumber,
        interactionTimestamp: getCurrentDateTimeAsString(),
        reversalAmount: interaction.paymentAmount,
        bonusXpress: interaction.bonusXpressCashValue ? {
          reversalCashValue: interaction.bonusXpressCashValue,
        } : undefined,
        coupons: interaction.selectedCoupons?.map((coupon) => ({
          couponCode: coupon.couponCode,
        })),
        receiptDetails: interaction.receiptDetails,
      },
      interaction.selectedCoupons
    );
  };

  if (!interaction) return null;
  const operationalUnit = operationalUnits.find(
    (operationalUnit) => operationalUnit.externalUnitNumber === interaction.externalUnitNumber
  );
  if (!operationalUnit) return null;
  const rootUnit = operationalUnits.find((operationalUnit) => !operationalUnit.parentUnitNumber);
  if (!rootUnit) return null;
  const amount = interaction.reversalAmount ? -interaction.reversalAmount : interaction.paymentAmount;
  const bonusXpressBonusPoints =
    interaction.reversalResponse?.bonusXpress?.bonusPoints || interaction.paymentResponse?.bonusXpress?.bonusPoints;
  const bonusXpressDisplayedCashValue =
    interaction.bonusXpressReversalCashValue ||
    (interaction.bonusXpressCashValue ? interaction.bonusXpressCashValue * -1 : undefined);
  const bonusXpressCalculatedCashValue =
    (interaction.bonusXpressReversalCashValue ? interaction.bonusXpressReversalCashValue * -1 : undefined) ||
    interaction.bonusXpressCashValue;
  return (
    <>
      <Toast
        style={{
          fontFamily: "monospace",
          fontSize: "11.375px",
          padding: 16,
          maxWidth: 364,
          width: 364,
        }}
      >
        <p style={{ textAlign: "center" }}>
          <big>
            <b>{getThemeDisplayName(pathToTheme())}</b>
          </big>
          <br />
          {operationalUnit.unitName}

          {operationalUnit.contact?.address && (
            <>
              <br />
              {operationalUnit.contact.address.streetName} {operationalUnit.contact.address.houseNumber}
              <br />
              {operationalUnit.contact.address.zipCode} {operationalUnit.contact.address.cityName}{" "}
            </>
          )}
        </p>
        {interaction.externalReferenceReceiptId && (
          <p style={{ textAlign: "center" }}>
            <b>STORNORECHNUNG FÜR BELEG {interaction.externalReferenceReceiptId}</b>
          </p>
        )}
        {interaction.externalCustomerId && (
          <>
            <div
              style={{
                textAlign: "right",
              }}
            >
              Kundennummer: {interaction.externalCustomerId}
            </div>
            <br />
          </>
        )}

        {interaction.receiptDetails?.map((detail) => (
          <div
            key={detail.itemInfo.refNumber}
            style={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div>{detail.itemInfo.supplierItemText?.padEnd(35)}</div>
            <div>{formatEuro(interaction.reversalAmount ? -Number(detail.salesPrice) : Number(detail.salesPrice))}</div>
          </div>
        ))}
        <p>------------------------------------------------</p>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div>ZWISCHENSUMME</div>
          <div>{formatEuro(Number(amount))}</div>
        </div>
        <p>------------------------------------------------</p>
        {interaction.selectedCoupons?.map((coupon) => (
          <div
            key={coupon.couponCode}
            style={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div>
              {formatOldCouponForReceipt(coupon)}
            </div>
            <div>{formatEuro(-(calculateCoupon(amount, coupon) || 0))}</div>
          </div>
        ))}
        {interaction.selectedCoupon && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div>
              {formatCouponForReceipt(interaction.selectedCoupon)}
            </div>
            <div>{formatEuro(-(calculateNewCouponSavings(interaction.selectedCoupon, amount) || 0))}</div>
          </div>
        )}
        {bonusXpressBonusPoints && bonusXpressDisplayedCashValue && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div>Bonus-XPress ({Number(bonusXpressBonusPoints)} Punkte)</div>
            <div>{formatEuro(bonusXpressDisplayedCashValue)}</div>
          </div>
        )}

        {((bonusXpressBonusPoints && bonusXpressDisplayedCashValue) || interaction.coupons || interaction.selectedCoupon) && (
          <p>------------------------------------------------</p>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontWeight: "bold",
          }}
        >
          <div>SUMME</div>
          <div>
            {formatEuro(
              calculatePaymentAmountWithCouponAndXpress(
                amount,
                bonusXpressCalculatedCashValue,
                interaction.selectedCoupons,
                interaction.selectedCoupon
              )
            )}
          </div>
        </div>

        <p>================================================</p>
        <p>
          ************************************************
          <br />* {formatBonusPoints(interaction.paymentResponse, interaction.reversalResponse)}
          *
          <br />* {formatTotalBonusPoints(interaction.paymentResponse, interaction.reversalResponse)}
          *
          <br />
          ************************************************
        </p>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontWeight: "bold",
          }}
        >
          <div>
            Filiale: {interaction.externalUnitNumber}
            <br />
            Bon-Nr.: {interaction.externalReceiptId}
          </div>
          <div style={{ textAlign: "right" }}>
            {formatGermanDate(interaction.interactionTimestamp)}
            <br />
            {formatGermanTime(interaction.interactionTimestamp)}
          </div>
        </div>
      </Toast>
      <Row>
        <Col md={6}>
          <Button type="submit" className="mt-3" onClick={() => props.thunkResetInteraction()}>
            Neuer Einkauf
          </Button>
          {!interaction.reversalResponse && (
            <>
              <Button style={{ marginLeft: 16 }} type="submit" className="mt-3" onClick={() => reversal(true)}>
                Einkauf stornieren
              </Button>

              <Button style={{ marginLeft: 16 }} type="submit" className="mt-3" onClick={() => reversal(false)}>
                Storno ohne Bon
              </Button>
            </>
          )}
        </Col>
      </Row>
    </>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators({ thunkResetInteraction, thunkCreateReversalInteraction }, dispatch);

const mapStateToProps = (state: IStore) => ({
  interaction: state.touchpoint,
  operationalUnits: state.operationalUnit.operationalUnits,
});

export default connect(mapStateToProps, mapDispatchToProps)(ReceiptSimulator);
